import React, { useState, useEffect } from 'react';
import { api } from '../../src/services/api-new';

// Configuration
const START_HOUR = 8; // 8 AM
const END_HOUR = 19; // 7 PM
const SLOT_HEIGHT = 100; // taller slots for premium feel
const TOTAL_HOURS = END_HOUR - START_HOUR;

interface AdminScheduleProps {
    onBack?: () => void;
    onStartConsultation?: (patientId: string, appointmentId: number) => void;
}

const AdminSchedule: React.FC<AdminScheduleProps> = ({ onBack, onStartConsultation }) => {
    const [currentDate] = useState(new Date());
    const [sidebarSearch, setSidebarSearch] = useState('');
    const [currentTimePos, setCurrentTimePos] = useState(0);

    const [doctors, setDoctors] = useState<any[]>([]);
    const [appointments, setAppointments] = useState<any[]>([]);
    const [waitlist, setWaitlist] = useState<any[]>([]);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setIsDataLoading(true);
            try {
                const [vetsData, appsData, waitlistData] = await Promise.all([
                    api.vets.getAll(),
                    api.appointments.getAll(),
                    api.appointments.getWaitlist()
                ]);
                setDoctors((vetsData as any) || []);
                setAppointments((appsData as any) || []);
                setWaitlist((waitlistData as any) || []);
            } catch (error) {
                console.error("Failed to fetch schedule data:", error);
            } finally {
                setIsDataLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleAddAppointment = async (e: React.FormEvent) => {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const patientInput = form.elements.namedItem('patient') as HTMLInputElement;
        const patientIdInput = form.elements.namedItem('patientId') as HTMLInputElement;
        const doctorIdInput = form.elements.namedItem('doctorId') as HTMLSelectElement;
        const typeInput = form.elements.namedItem('type') as HTMLSelectElement;
        const dateInput = form.elements.namedItem('date') as HTMLInputElement;
        const startTimeInput = form.elements.namedItem('startTime') as HTMLInputElement;
        const durationInput = form.elements.namedItem('duration') as HTMLInputElement;
        const notesInput = form.elements.namedItem('notes') as HTMLTextAreaElement;

        const newAppointmentData = {
            patient: patientInput?.value || '',
            pid: patientIdInput?.value || '',
            doctorId: doctorIdInput?.value || '',
            type: typeInput?.value || 'Check-up',
            date: dateInput?.value || new Date().toISOString().split('T')[0],
            start: parseInt(startTimeInput?.value || '9'),
            duration: parseInt(durationInput?.value || '1'),
            notes: notesInput?.value || '',
            status: 'Confirmed'
        };

        try {
            const result = await api.appointments.create(newAppointmentData) as any;

            // Update local state
            setAppointments(prev => [...prev, result.appointment]);
            setIsAppointmentModalOpen(false);
        } catch (error) {
            console.error("Failed to add appointment:", error);
            alert("Failed to add appointment");
        }
    };

    const handleUpdateStatus = async (id: number, status: string) => {
        try {
            await api.appointments.updateStatus(id, status);
            // Refresh data
            const [vetsData, appsData, waitlistData] = await Promise.all([
                api.vets.getAll(),
                api.appointments.getAll(),
                api.appointments.getWaitlist()
            ]);
            setDoctors((vetsData as any) || []);
            setAppointments((appsData as any) || []);
            setWaitlist((waitlistData as any) || []);
        } catch (error) {
            console.error("Failed to update status:", error);
            alert("Failed to update status");
        }
    };

    useEffect(() => {
        const updateTime = () => {
            const now = new Date();
            let hours = now.getHours() + now.getMinutes() / 60;
            if (hours < START_HOUR || hours > END_HOUR) hours = 10.5; // Demo fallback
            setCurrentTimePos((hours - START_HOUR) * SLOT_HEIGHT);
        };
        updateTime();
        const interval = setInterval(updateTime, 60000);
        return () => clearInterval(interval);
    }, []);

    const filteredWaitlist = waitlist.filter(w =>
        w.name.toLowerCase().includes(sidebarSearch.toLowerCase()) ||
        w.pid.toLowerCase().includes(sidebarSearch.toLowerCase())
    );

    if (isDataLoading) {
        return (
            <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-background-dark">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Calibrating Timeline...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-slate-50 dark:bg-background-dark overflow-hidden">
            {/* Toolbar - Glassmorphism */}
            <div className="h-20 bg-white/80 dark:bg-surface-dark/80 backdrop-blur-xl border-b border-gray-100 dark:border-gray-800 flex items-center justify-between px-8 shrink-0 z-30 shadow-sm relative">
                <div className="flex items-center gap-8">
                    <div>
                        <h2 className="text-2xl font-black flex items-center gap-3 text-primary-dark dark:text-white tracking-tight">
                            <span className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary"><span className="material-icons-round">calendar_month</span></span>
                            Daily Operations
                        </h2>
                    </div>
                    <div className="flex bg-gray-100 dark:bg-gray-800 rounded-2xl p-1 border border-gray-100 dark:border-gray-700 shadow-inner">
                        <button className="px-6 py-1.5 bg-white dark:bg-surface-dark shadow-xl rounded-xl text-[10px] font-black uppercase tracking-widest text-primary-dark dark:text-white">Timeline</button>
                        <button className="px-6 py-1.5 text-gray-400 hover:text-gray-900 dark:hover:text-white text-[10px] font-black uppercase tracking-widest transition-all">Grid</button>
                    </div>
                    <div className="flex items-center gap-4 text-xs font-black text-gray-400 uppercase tracking-widest">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 hover:border-primary transition-all shadow-sm mr-2"
                            >
                                <span className="material-icons-round text-gray-600 dark:text-gray-300">arrow_back</span>
                            </button>
                        )}
                        <button className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"><span className="material-icons-round text-lg">chevron_left</span></button>
                        <span className="text-gray-600 dark:text-gray-200">{currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
                        <button className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"><span className="material-icons-round text-lg">chevron_right</span></button>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setIsAppointmentModalOpen(true)}
                        className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-primary/30 transition-all flex items-center gap-2 active:scale-95"
                    >
                        <span className="material-icons-round text-sm">post_add</span> New Entry
                    </button>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">

                {/* Main Calendar Area */}
                <div className="flex-1 flex flex-col overflow-hidden relative bg-white dark:bg-background-dark">

                    {/* Doctor Headers - Sticky Top */}
                    <div className="flex border-b border-gray-100 dark:border-gray-800 bg-white/50 dark:bg-surface-dark/50 backdrop-blur-md shrink-0 pl-20 overflow-hidden pr-4 overflow-x-auto no-scrollbar">
                        {doctors.map(doc => (
                            <div key={doc.id} className="flex-1 p-5 border-r border-gray-100 dark:border-gray-800 flex items-center gap-4 min-w-[220px]">
                                <div className="relative group">
                                    <img src={doc.image} className="w-12 h-12 rounded-2xl object-cover shadow-lg transition-transform group-hover:scale-110" alt={doc.name} />
                                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white dark:border-surface-dark rounded-full shadow-md"></div>
                                </div>
                                <div>
                                    <h3 className="font-black text-sm text-primary-dark dark:text-white leading-tight">{doc.name}</h3>
                                    <p className="text-[9px] font-black text-primary uppercase tracking-widest mt-0.5">{doc.specialty}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Timeline Grid */}
                    <div className="flex-1 overflow-auto relative custom-scrollbar scroll-smooth no-scrollbar">
                        <div className="flex min-w-full" style={{ height: TOTAL_HOURS * SLOT_HEIGHT }}>

                            {/* Time Axis - Sticky Left */}
                            <div className="w-20 shrink-0 border-r border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-surface-dark/80 backdrop-blur-md z-20 sticky left-0 shadow-xl shadow-black/5">
                                {Array.from({ length: TOTAL_HOURS }).map((_, i) => (
                                    <div key={i} className="border-b border-gray-50 dark:border-gray-800/50 text-[10px] text-gray-400 font-black text-right pr-4 pt-3 relative" style={{ height: SLOT_HEIGHT }}>
                                        <span className="relative -top-1 opacity-60">{`${START_HOUR + i}:00`}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Grid Columns */}
                            <div className="flex-1 flex relative min-w-fit bg-slate-50/30 dark:bg-black/10">

                                {/* Horizontal Grid Lines */}
                                <div className="absolute inset-0 w-full pointer-events-none z-0">
                                    {Array.from({ length: TOTAL_HOURS }).map((_, i) => (
                                        <div key={i} className="border-b border-gray-100 dark:border-gray-800/50 w-full" style={{ height: SLOT_HEIGHT }}>
                                            <div className="w-full h-1/2 border-b border-dashed border-gray-50 dark:border-gray-800/20"></div>
                                        </div>
                                    ))}
                                    {/* Current Time Indicator */}
                                    <div
                                        className="absolute left-0 w-full border-t-2 border-primary z-20 pointer-events-none flex items-center"
                                        style={{ top: currentTimePos }}
                                    >
                                        <div className="w-3 h-3 bg-primary rounded-full -ml-1.5 shadow-lg shadow-primary/50 ring-4 ring-primary/20 animate-pulse"></div>
                                        <div className="bg-primary text-white text-[8px] font-black px-2 py-0.5 rounded-full ml-2 shadow-lg shadow-primary/30 uppercase tracking-tighter">Live</div>
                                    </div>
                                </div>

                                {/* Doctor Columns */}
                                {doctors.map(doc => (
                                    <div key={doc.id} className="flex-1 border-r border-gray-100 dark:border-gray-800 relative min-w-[220px]">
                                        {/* Appointments */}
                                        {appointments.filter(app => app.vet_id === doc.id).map(app => (
                                            <AppointmentCard
                                                key={app.id}
                                                data={app}
                                                slotHeight={SLOT_HEIGHT}
                                                startHour={START_HOUR}
                                                onStartConsultation={onStartConsultation}
                                                onUpdateStatus={handleUpdateStatus}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar: Waitlist */}
                <div className="hidden xl:flex w-96 bg-white dark:bg-surface-dark border-l border-gray-100 dark:border-gray-800 flex-col z-30 shadow-2xl overflow-hidden shadow-black/10">
                    <div className="p-8 border-b border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-black/20">
                        <div className="flex justify-between items-end mb-6">
                            <div>
                                <p className="text-[10px] font-black text-primary uppercase tracking-widest mb-1">Clinic Queue</p>
                                <h3 className="text-xl font-black text-primary-dark dark:text-white tracking-tight">Current Waitlist</h3>
                            </div>
                            <span className="bg-primary text-white text-[10px] font-black px-3 py-1.5 rounded-full shadow-lg shadow-primary/20">{filteredWaitlist.length} Patients</span>
                        </div>
                        <div className="relative">
                            <span className="material-icons-round absolute left-4 top-3.5 text-gray-400 text-lg">search</span>
                            <input
                                className="w-full bg-white dark:bg-background-dark border border-gray-100 dark:border-gray-700 rounded-2xl text-xs py-4 pl-12 pr-4 font-bold focus:ring-4 focus:ring-primary/10 outline-none transition-all shadow-sm placeholder:opacity-50"
                                placeholder="Find checked-in patients..."
                                value={sidebarSearch}
                                onChange={(e) => setSidebarSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-white dark:bg-surface-dark custom-scrollbar no-scrollbar">
                        {filteredWaitlist.map(item => (
                            <div key={item.id} className="group bg-slate-50/50 dark:bg-background-dark/50 p-5 rounded-3xl border border-gray-50 dark:border-gray-800 hover:border-primary/30 hover:shadow-xl hover:shadow-black/5 hover:-translate-y-1 transition-all cursor-grab active:cursor-grabbing">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-white dark:bg-surface-dark shadow-sm flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">{item.icon}</div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-start">
                                            <h4 className="font-black text-sm text-primary-dark dark:text-white tracking-tight">{item.name}</h4>
                                            <span className={`text-[8px] font-black px-2 py-1 rounded-full uppercase tracking-tighter ${item.tag === 'Urgent' ? 'bg-red-500 text-white animate-pulse' : 'bg-primary/10 text-primary'}`}>
                                                {item.tag}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-gray-400 font-bold mb-3">{item.pid} • {item.type}</p>
                                        <div className="bg-white/50 dark:bg-black/20 p-3 rounded-xl mb-4">
                                            <p className="text-[10px] text-gray-500 font-medium italic">"{item.reason}"</p>
                                        </div>
                                        {item.status === 'PENDING' && (
                                            <button
                                                onClick={() => handleUpdateStatus(item.id, 'CONFIRMED')}
                                                className="w-full bg-primary text-white py-2 rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-95 transition-all"
                                            >
                                                Confirm Booking
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* MODAL: ADD NEW APPOINTMENT */}
            {isAppointmentModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-primary-dark/40 backdrop-blur-xl animate-fade-in" onClick={() => setIsAppointmentModalOpen(false)}>
                    <div className="bg-white dark:bg-surface-dark w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scale-up" onClick={e => e.stopPropagation()}>
                        <div className="p-10 border-b border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-black/20 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black text-primary uppercase tracking-[0.3em] mb-1">New Entry</p>
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Schedule Appointment</h2>
                            </div>
                            <button onClick={() => setIsAppointmentModalOpen(false)} className="w-12 h-12 rounded-2xl hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-center text-gray-400"><span className="material-icons-round">close</span></button>
                        </div>

                        <form onSubmit={handleAddAppointment} className="flex-1 overflow-y-auto p-10 space-y-8 custom-scrollbar">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Patient Name</label>
                                    <input
                                        name="patient"
                                        required
                                        type="text"
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-lg font-black outline-none transition-all placeholder:text-gray-300"
                                        placeholder="e.g. Max, Bella, Charlie"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Patient ID</label>
                                    <input
                                        name="patientId"
                                        required
                                        type="text"
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all placeholder:text-gray-300"
                                        placeholder="e.g. P001, P002"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Veterinarian</label>
                                    <select
                                        name="doctorId"
                                        required
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                    >
                                        <option value="">Select Doctor</option>
                                        {doctors.map(doc => (
                                            <option key={doc.id} value={doc.id}>{doc.name} - {doc.specialty}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Appointment Type</label>
                                    <select
                                        name="type"
                                        required
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                    >
                                        <option value="Check-up">Check-up</option>
                                        <option value="Vaccine">Vaccine</option>
                                        <option value="Surgery">Surgery</option>
                                        <option value="Exotic Care">Exotic Care</option>
                                        <option value="Skin Allergy">Skin Allergy</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Date</label>
                                    <input
                                        name="date"
                                        required
                                        type="date"
                                        defaultValue={new Date().toISOString().split('T')[0]}
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Start Time</label>
                                    <select
                                        name="startTime"
                                        required
                                        className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                    >
                                        {Array.from({ length: 11 }, (_, i) => i + 8).map(hour => (
                                            <option key={hour} value={hour}>{hour}:00</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Duration (hours)</label>
                                <select
                                    name="duration"
                                    required
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-5 rounded-2xl text-sm font-black outline-none transition-all"
                                >
                                    <option value={1}>1 hour</option>
                                    <option value={2}>2 hours</option>
                                    <option value={3}>3 hours</option>
                                    <option value={0.5}>30 minutes</option>
                                </select>
                            </div>

                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Notes</label>
                                <textarea
                                    name="notes"
                                    rows={3}
                                    className="w-full bg-slate-50 dark:bg-background-dark border border-transparent focus:border-primary/30 focus:bg-white p-6 rounded-3xl text-sm font-medium outline-none transition-all resize-none"
                                    placeholder="Additional notes or special requirements..."
                                ></textarea>
                            </div>
                        </form>

                        <div className="p-10 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-4 bg-slate-50/50 dark:bg-black/20">
                            <button type="button" onClick={() => setIsAppointmentModalOpen(false)} className="px-8 py-4 font-black text-[10px] text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">Cancel</button>
                            <button type="submit" className="bg-primary text-white px-10 py-5 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 transition-all hover:-translate-y-1 active:scale-95">Schedule Appointment</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AppointmentCard = ({ data, slotHeight, startHour, onStartConsultation, onUpdateStatus }: any) => {
    // data.appointment_date is like "2026-02-14 09:00"
    const timeMatch = data.appointment_date.match(/(\d{2}):(\d{2})$/);
    const hours = timeMatch ? parseInt(timeMatch[1]) + (parseInt(timeMatch[2]) / 60) : startHour;

    const top = (hours - startHour) * slotHeight;
    const height = (data.duration || 1) * slotHeight;

    const theme: any = {
        'Surgery': 'bg-red-500/10 border-red-500 text-red-600 dark:text-red-400 shadow-red-500/5',
        'Check-up': 'bg-blue-500/10 border-blue-500 text-blue-600 dark:text-blue-400 shadow-blue-500/5',
        'Vaccine': 'bg-green-500/10 border-green-500 text-green-600 dark:text-green-400 shadow-green-500/5',
        'Exotic Care': 'bg-purple-500/10 border-purple-500 text-purple-600 dark:text-purple-400 shadow-purple-500/5',
        'Skin Allergy': 'bg-orange-500/10 border-orange-500 text-orange-600 dark:text-orange-400 shadow-orange-500/5',
    };

    const themeClass = theme[data.type] || theme['Check-up'];

    const handleCancel = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to cancel this appointment?')) {
            onUpdateStatus(data.id, 'CANCELLED');
        }
    };

    return (
        <div
            className={`absolute left-2 right-2 rounded-2xl border-l-4 p-4 shadow-lg backdrop-blur-sm cursor-pointer hover:shadow-2xl hover:scale-[1.02] transition-all overflow-hidden flex flex-col justify-between z-10 group ${themeClass}`}
            style={{ top: `${top + 4}px`, height: `${height - 8}px` }}
        >
            <div className="relative z-10">
                <div className="flex justify-between items-center mb-1">
                    <span className="text-[9px] font-black uppercase tracking-[0.1em] opacity-80">{data.type}</span>
                    <div className="flex items-center gap-1.5 font-black text-[9px] uppercase tracking-tighter opacity-80">
                        <span className="material-icons-round text-xs">fingerprint</span>
                        {data.pid}
                    </div>
                </div>
                <h4 className="font-black text-sm tracking-tight leading-tight group-hover:text-primary transition-colors">{data.patient}</h4>
            </div>

            <div className="flex items-center justify-between mt-auto">
                <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${data.status === 'ANAMNESA' ? 'bg-orange-500 animate-pulse' : data.status === 'CONFIRMED' ? 'bg-blue-500' : data.status === 'MEDICAL_RECORD_COMPLETED' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                    <span className="text-[9px] font-black uppercase tracking-widest opacity-60">{data.status}</span>
                </div>
                <div className="flex items-center gap-1">
                    {onStartConsultation && data.patient_id && data.status === 'CONFIRMED' && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onStartConsultation(data.patient_id, data.id);
                            }}
                            className="bg-primary/20 hover:bg-primary/40 text-primary px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all flex items-center gap-1"
                        >
                            <span className="material-icons-round text-xs">play_arrow</span>
                            Start
                        </button>
                    )}
                    {onUpdateStatus && data.status === 'CONFIRMED' && (
                        <button
                            onClick={handleCancel}
                            className="bg-red-500/10 hover:bg-red-500/20 text-red-500 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all flex items-center gap-1"
                            title="Cancel Appointment"
                        >
                            <span className="material-icons-round text-xs">close</span>
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            {/* Background Decorative Pattern */}
            <div className="absolute top-0 right-0 -mr-4 -mt-4 opacity-[0.03] rotate-12">
                <span className="material-icons-round text-7xl select-none">medication</span>
            </div>
        </div>
    );
};

export default AdminSchedule;
